import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useCompany } from '@/context/companies'
import { useUpdatePaymentRecordStatus } from '@/api/accounting-dealer'
import { AdditionalService } from '@/types'
import {
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { formatCurrency, formatDate, toast } from '@/helpers'
interface ServiceCardProps {
  data: AdditionalService
}

export function ServiceCard({ data }: ServiceCardProps) {
  const { company } = useCompany()

  const { key, amount, description, status, installments, cashflow_date } = data

  const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down('sm'))

  const [isApproveDialogOpen, setIsApproveDialogOpen] = useState(false)
  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false)
  const [rejectReason, setRejectReason] = useState('')

  const queryClient = useQueryClient()

  const {
    mutateAsync: updatePaymentRecordStatus,
    isPending: updatingPaymentRecordStatus,
  } = useUpdatePaymentRecordStatus({
    onSuccess: () => {
      toast.success('Serviço adicional aprovado com sucesso!')

      queryClient.invalidateQueries({
        queryKey: [
          'payment-records',
          { status: 'waiting', deal_key: company.deal.key },
        ],
      })

      queryClient.invalidateQueries({
        queryKey: [
          'payment-records',
          { status: 'accepted', deal_key: company.deal.key },
        ],
      })
    },
  })

  const toggleApproveDialog = () => setIsApproveDialogOpen((prev) => !prev)
  const toggleRejectDialog = () => setIsRejectDialogOpen((prev) => !prev)

  const handleApprove = () => {
    updatePaymentRecordStatus({
      deal_key: company.deal.key,
      payment_record_key: key,
      status: 'accepted',
    })
  }

  const handleReject = () => {
    updatePaymentRecordStatus({
      deal_key: company.deal.key,
      payment_record_key: key,
      status: 'rejected',
      reject_reason: rejectReason,
    })
  }

  const isPending = status === 'waiting'

  return (
    <>
      <Card>
        {isPending && isMobile && (
          <Chip label="Pendente" color="warning" size="small" sx={{ mb: 1 }} />
        )}

        <Box display="flex" justifyContent="space-between">
          <Box flex={1}>
            <Typography variant="h5">{description}</Typography>
            <Typography variant="body2" mt={1}>
              Data da fatura: {formatDate(cashflow_date, 'MM/yyyy')}
            </Typography>
          </Box>

          <Typography fontSize={[20, 24]} textAlign="right">
            {formatCurrency(amount)}
            {installments && (
              <Typography component="span" variant="body2" display="block">
                {installments}x
                {formatCurrency(amount, {
                  style: 'decimal',
                })}
              </Typography>
            )}
          </Typography>
        </Box>

        {isPending && (
          <Box
            display="flex"
            justifyContent={['space-between', 'flex-end']}
            mt={2}
            gap={1}
          >
            {isPending && !isMobile && (
              <Box flex={1}>
                <Chip label="Pendente" color="warning" />
              </Box>
            )}
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={toggleRejectDialog}
              sx={{ width: ['100%', 'auto'] }}
            >
              Recusar
            </Button>
            <Button
              variant="outlined"
              color="success"
              size="small"
              onClick={toggleApproveDialog}
              sx={{ width: ['100%', 'auto'] }}
            >
              Aceitar
            </Button>
          </Box>
        )}
      </Card>

      <Dialog
        open={isApproveDialogOpen}
        onClose={toggleApproveDialog}
        maxWidth="xs"
      >
        <DialogTitle>Aceitar serviço adicional?</DialogTitle>
        <DialogActions>
          <Button variant="text" onClick={toggleApproveDialog}>
            Cancelar
          </Button>
          <LoadingButton
            color="success"
            onClick={handleApprove}
            loading={updatingPaymentRecordStatus}
          >
            Aceitar
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isRejectDialogOpen}
        onClose={toggleRejectDialog}
        maxWidth="xs"
      >
        <DialogTitle>Recusar serviço adicional?</DialogTitle>
        <DialogContent>
          <TextField
            label="Motivo"
            multiline
            rows={3}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={toggleRejectDialog}>
            Cancelar
          </Button>
          <LoadingButton
            color="error"
            onClick={handleReject}
            loading={updatingPaymentRecordStatus}
          >
            Recusar
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  )
}
